import { useState } from 'react'
import { Alert, Box, Button, InputAdornment, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import { Search } from 'lucide-react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { AppShell, Crumb, Eyebrow, Mono, SkeletonCards, Surface, formatDateTime, get, tokens } from '@agribot/polygon-checks-ui'

interface Entry {
  id: number
  occurred_at: string
  user_id: string | null
  user_name: string | null
  user_email: string | null
  entity: string
  entity_id: string
  action: string
  detail: Record<string, unknown>
}

/** What a record type is called on screen; anything else shows as stored. */
const ENTITIES: { value: string; label: string }[] = [
  { value: 'submission', label: 'Submissions' },
  { value: 'origin', label: 'Origins' },
  { value: 'material', label: 'Materials' },
  { value: 'campaign', label: 'Campaigns' },
  { value: 'commodity', label: 'Commodities' },
  { value: 'fraction', label: 'Ingredients' },
  { value: 'supplier', label: 'Suppliers' },
  { value: 'parent_company', label: 'Parent companies' },
  { value: 'user', label: 'People' },
  { value: 'plot', label: 'Plots' },
  { value: 'region', label: 'Regions' },
]

const PAGE = 50
const summary = (detail: Record<string, unknown>) =>
  Object.entries(detail ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ')

/**
 * Every change anyone made in the organisation, newest first: who (name and
 * email), when, to what, and what changed. The RFI asks for this log to be
 * visible to the buyer's administrators; it is the same audit trail behind
 * the "changed by" tooltips and the export's audit columns.
 */
export function Activity() {
  const [entity, setEntity] = useState('')
  const [q, setQ] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const params = (before?: number) => new URLSearchParams({
    limit: String(PAGE),
    ...(entity ? { entity } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(since ? { since: new Date(`${since}T00:00:00`).toISOString() } : {}),
    ...(until ? { until: new Date(`${until}T23:59:59`).toISOString() } : {}),
    ...(before ? { before: String(before) } : {}),
  })
  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['org-audit', entity, q.trim(), since, until],
    queryFn: ({ pageParam }) => get<{ entries: Entry[]; next_before: number | null }>(`/org/audit?${params(pageParam ?? undefined)}`),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.next_before,
  })
  const entries = data?.pages.flatMap((p) => p.entries) ?? []
  const label = (e: string) => ENTITIES.find((x) => x.value === e)?.label.replace(/s$/, '') ?? e.replace(/_/g, ' ')
  const cols = '170px minmax(0,1.2fr) 150px minmax(0,2.4fr)'

  return (
    <AppShell crumbs={[<Crumb key="a">Admin</Crumb>, <Crumb key="b">Activity</Crumb>]}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Admin</Eyebrow>
        <Typography variant="h1" sx={{ mb: 2 }}>Activity</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 720 }}>
          Every change made in your organisation, by your people and by your suppliers: who, when, to what, and what changed.
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search people and details"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Search people and details' }}
            sx={{ width: 280 }}
          />
          <TextField select value={entity} onChange={(e) => setEntity(e.target.value)} SelectProps={{ displayEmpty: true }} inputProps={{ 'aria-label': 'Record type' }} sx={{ width: 200 }}>
            <MenuItem value="">Everything</MenuItem>
            {ENTITIES.map((e) => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
          </TextField>
          <TextField type="date" label="From" value={since} onChange={(e) => setSince(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
          <TextField type="date" label="To" value={until} onChange={(e) => setUntil(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
        </Box>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{(error as Error).message}</Alert>}
        <Surface sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 4, px: 5, py: 3, borderBottom: `1px solid ${tokens.color.ink200}` }}>
            <Eyebrow>When</Eyebrow><Eyebrow>Who</Eyebrow><Eyebrow>What</Eyebrow><Eyebrow>Change</Eyebrow>
          </Box>
          {isPending && <SkeletonCards columns={cols} />}
          {!isPending && entries.length === 0 && <Typography variant="body2" sx={{ px: 5, py: 6, color: 'text.secondary' }}>Nothing matches.</Typography>}
          {entries.map((e) => (
            <Box key={e.id} sx={{ display: 'grid', gridTemplateColumns: cols, gap: 4, alignItems: 'start', px: 5, py: 3, borderBottom: `1px solid ${tokens.color.ink100}` }}>
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(e.occurred_at)}</Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{e.user_name ?? 'System'}</Typography>
                {e.user_email && <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">{e.user_email}</Typography>}
              </Box>
              <Box>
                <Typography variant="body2">{label(e.entity)} · {e.action.replace(/_/g, ' ')}</Typography>
                <Tooltip title={e.entity_id}><Mono sx={{ fontSize: 11, color: 'text.secondary' }}>{e.entity_id.slice(0, 8)}</Mono></Tooltip>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>{summary(e.detail) || '—'}</Typography>
            </Box>
          ))}
        </Surface>
        {hasNextPage && (
          <Button variant="outlined" sx={{ mt: 4 }} disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
            {isFetchingNextPage ? 'Loading…' : 'Load older'}
          </Button>
        )}
      </Box>
    </AppShell>
  )
}
